/* eslint-disable sonarjs/cognitive-complexity */
import { type FC, useMemo } from 'react';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { DateInput } from 'src/modules/shared/components/atoms/inputs/date-input';
import MultiSelect from 'src/modules/shared/components/atoms/inputs/multi-select';
import { TagInput } from 'src/modules/shared/components/atoms/inputs/tag-input';
import { SearcherDocumentSearchFilterOperator } from 'src/api/generated';
import {
  type FilterFieldOptions,
  type FilterValue,
  type FilterOption,
} from 'src/modules/shared/types/filters.types';
import {
  isArrayOperator,
  isNoValueOperator,
} from 'src/modules/shared/utils/filters/operators.util';
import { formatLegacyAwareCustomFieldDateInputValue } from 'src/modules/shared/utils/custom-field-date-legacy.util';

export interface FilterValueInputProps {
  field?: FilterFieldOptions;
  operator?: SearcherDocumentSearchFilterOperator;
  value?: FilterValue;
  onChange: (value: FilterValue) => void;
  options?: FilterOption[];
  loading?: boolean;
  onQueryChange?: (query: string) => void;
  clientFilter?: boolean;
}

const toSelectOption = (option: FilterOption) => ({
  content: 'name' in option ? option.name : option.label ?? option.value,
  value: 'value' in option ? String(option.value) : String(option.id),
});

export const FilterValueInput: FC<FilterValueInputProps> = ({
  field,
  operator,
  value,
  onChange,
  options = [],
  loading = false,
  onQueryChange,
  clientFilter,
}) => {
  const selectOptions = useMemo(() => options.map(toSelectOption), [options]);
  const responsiveValueContainerClassName = 'w-full sm:min-w-0';

  if (!field || !operator || isNoValueOperator(operator)) {
    return null;
  }

  const isArrayOp = isArrayOperator(operator);

  if (field.type === 'boolean') {
    // TODO: check when implementing i18n
    const valueString =
      value !== true && value !== false ? 'Verdadero' : String(value);

    return (
      <Select
        // TODO: check when implementing i18n
        options={[
          { content: 'Verdadero', value: 'true' },
          { content: 'Falso', value: 'false' },
        ]}
        size="sm"
        variant="primary"
        value={valueString}
        onChange={(e) => onChange(e.target.value === 'true')}
        containerClassName={responsiveValueContainerClassName}
      />
    );
  }

  if (
    field.type === 'user' ||
    field.type === 'list' ||
    field.type === 'status' ||
    field.type === 'property'
  ) {
    if (isArrayOp) {
      return (
        <div className={responsiveValueContainerClassName}>
          <MultiSelect
            options={selectOptions}
            size="sm"
            value={Array.isArray(value) ? value.map(String) : [String(value)]}
            onChange={(values) => onChange(values)}
            containerClassName="flex-1"
          />
        </div>
      );
    }

    const selectableValue = value == null ? undefined : String(value);

    return (
      <SearchableSelect
        options={selectOptions}
        size="sm"
        variant="primary"
        value={selectableValue}
        onChange={(val) => {
          if (val === undefined) return;
          if (field.type === 'property') {
            const parsed = Number(val);
            if (!Number.isFinite(parsed)) return;
            onChange(parsed);
            return;
          }
          onChange(val);
        }}
        onQueryChange={onQueryChange}
        clientFilter={clientFilter}
        loading={loading}
        // TODO: check when implementing i18n
        placeholder="Selecciona o busca..."
        containerClassName={responsiveValueContainerClassName}
      />
    );
  }

  if (field.type === 'date') {
    const useDateTimeInput =
      operator !== SearcherDocumentSearchFilterOperator.EQ || field.includeTime;

    return (
      <div className={responsiveValueContainerClassName}>
        <DateInput
          type={useDateTimeInput ? 'datetime-local' : 'date'}
          size="sm"
          variant="primary"
          value={formatLegacyAwareCustomFieldDateInputValue(
            value,
            Boolean(useDateTimeInput)
          )}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (isArrayOp) {
    return (
      <div className={responsiveValueContainerClassName}>
        <TagInput
          size="sm"
          variant="primary"
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={(values) => {
            if (field.type === 'number') {
              const numberValues = values.map(Number).filter((n) => !isNaN(n));
              onChange(numberValues);
            } else {
              onChange(values);
            }
          }}
          validateValue={(value) => {
            if (field.type === 'number') {
              const num = Number(value);
              return !isNaN(num);
            }
            return true;
          }}
          formatValue={(value) => {
            if (field.type === 'number') {
              return String(Number(value));
            }
            return value;
          }}
        />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <div className={responsiveValueContainerClassName}>
        <TextInput
          type="number"
          size="sm"
          variant="primary"
          value={value as number}
          onChange={(e) => onChange(Number(e.target.value))}
          // TODO: check when implementing i18n
          placeholder="Ingresa un valor"
        />
      </div>
    );
  }

  return (
    <div className={responsiveValueContainerClassName}>
      <TextInput
        size="sm"
        variant="primary"
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        // TODO: check when implementing i18n
        placeholder="Ingresa un texto"
      />
    </div>
  );
};
